import { Box, Card, CardContent, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import type { PaymentSummaryCardView } from "./types";
import { usePaymentModeSummary, type UsePaymentModeSummaryConfig } from "./usePaymentModeSummary";

export type ReceiptPaymentModeSummaryProps = UsePaymentModeSummaryConfig & {
  cardsFromResponse: (
    summary: NonNullable<ReturnType<typeof usePaymentModeSummary>["summary"]>
  ) => PaymentSummaryCardView[];
};

export default function ReceiptPaymentModeSummary({
  cardsFromResponse,
  ...fetchConfig
}: ReceiptPaymentModeSummaryProps) {
  const { summary, loading } = usePaymentModeSummary(fetchConfig);

  if (!summary || loading) {
    return null;
  }

  const cards = cardsFromResponse(summary);

  return (
    <Box sx={{ mb: { xs: 2, sm: 3 }, px: { xs: 0, sm: 1 } }}>
      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{ fontWeight: "bold", mb: 1.5, fontSize: { xs: "0.95rem", sm: "1.1rem" } }}
      >
        Payment Summary
      </Typography>
      <Grid container spacing={1}>
        {cards.map((card) => (
          <Grid key={card.key} size={{ xs: 12, sm: 6, lg: 3 }}>
            <Card
              sx={(theme) => ({
                p: 0,
                bgcolor: alpha(theme.palette[card.tone].main, 0.12),
                ...(card.tone === "success"
                  ? { borderTop: `2px solid ${theme.palette.success.main}` }
                  : {}),
              })}
            >
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {card.label}
                </Typography>
                <Typography
                  variant="h6"
                  sx={(theme) => ({
                    fontWeight: "bold",
                    ...(card.tone === "success" ? { color: theme.palette.success.dark } : {}),
                  })}
                >
                  {card.totalFormatted}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.countLabel}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
